import React, { useState, useEffect, useRef } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useStripe } from '@stripe/react-stripe-js'
import * as Sentry from '@sentry/react'
import CustomModal from '../../CustomModal/CustomModal'
import { PLANS, PLANS_ID, getMergedPlan } from '../../../constants/plans'
import { switchPlan } from '../../../services/manage'
import { addUpgradeReason, getUpgradeReason } from '../../../utils/general'
import { fetchUserData, showMessageModal, showUpgradeModal } from '../../../Manage/actions'
import { handle3DSecure } from '../../../utils/payment'

const DEFAULT_TITLE = "You can't add custom domains on the free trial"
const DEFAULT_DESCRIPTION = 'Upgrade your account to add a custom domain.'
const DEFAULT_CONFIRM_DESCRIPTION = (planName) =>
  `Please confirm you want to upgrade now to the ${planName} plan to add a custom domain.`
const DEFAULT_SUCCESS_MESSAGE = (planLabel) =>
  `Your account has been upgraded to ${planLabel}. You can now add custom domains.`

const UpgradePromptModal = ({
  show,
  onHide,
  userProfile,
  dispatch,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  confirmDescription = DEFAULT_CONFIRM_DESCRIPTION,
  successMessage = DEFAULT_SUCCESS_MESSAGE
}) => {
  const [showConfirm, setShowConfirm] = useState(false)
  const [isUpgrading, setIsUpgrading] = useState(false)
  const stripe = useStripe()

  const isMountedRef = useRef(true)

  useEffect(() => {
    if (show) addUpgradeReason('Add Custom Domain')
  }, [show])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const selectedUpgradePlan = PLANS_ID[userProfile?.productId] || PLANS.TINY
  const upgradePlanId = selectedUpgradePlan.id
  const planName = selectedUpgradePlan.label

  const handleUpgrade = async () => {
    if (isMountedRef.current) setIsUpgrading(true)

    try {
      const upgradePlan = getMergedPlan(upgradePlanId, userProfile?.currency)
      const upgradeReason = getUpgradeReason()
      const period = userProfile?.interval === 'month' ? 'monthly' : 'yearly'
      const priceId = upgradePlan?.prices?.[period]?.stripe?.priceId
      if (!priceId) {
        throw new Error(`Missing priceId for ${upgradePlanId} (${period})`)
      }

      const formData = {
        email: userProfile?.email,
        productId: upgradePlanId,
        upgradeReason,
        endTrial: true,
        priceId
      }

      const { data } = await switchPlan(formData)

      if (data?.requires_action) {
        if (stripe) {
          await handle3DSecure(
            stripe,
            data,
            userProfile?.email,
            () => {
              if (!isMountedRef.current) return
              dispatch(fetchUserData(false))
              dispatch(
                showMessageModal({
                  title: 'Upgrade successful',
                  message: successMessage(upgradePlan?.label || planName),
                  error: false
                })
              )
              onHide()
              setIsUpgrading(false)
              setShowConfirm(false)
            },
            (errorMessage) => {
              Sentry.captureMessage(`3DS failed: ${errorMessage}`, {
                level: 'error',
                tags: { section: 'upgrade-prompt-3ds' }
              })
              dispatch(
                showMessageModal({
                  title: 'Upgrade failed',
                  message: errorMessage || 'Payment authentication failed. Please try again.',
                  error: true
                })
              )
              if (!isMountedRef.current) return
              onHide()
              setIsUpgrading(false)
              setShowConfirm(false)
            }
          )
        } else {
          if (!isMountedRef.current) return
          onHide()
          dispatch(showUpgradeModal(true))
          setIsUpgrading(false)
          setShowConfirm(false)
        }
      } else {
        if (!isMountedRef.current) return
        dispatch(fetchUserData(false))
        dispatch(
          showMessageModal({
            title: 'Upgrade successful',
            message: successMessage(upgradePlan?.label || planName),
            error: false
          })
        )
        onHide()
        setIsUpgrading(false)
        setShowConfirm(false)
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { section: 'upgrade-prompt-error' }
      })
      dispatch(
        showMessageModal({
          title: 'Upgrade failed',
          message: error?.message || 'Failed to process upgrade. Please try again or contact support.',
          error: true
        })
      )
      if (!isMountedRef.current) return
      onHide()
      setIsUpgrading(false)
      setShowConfirm(false)
    }
  }

  const handleClose = () => {
    setShowConfirm(false)
    onHide()
  }

  return (
    <CustomModal show={show} onHide={handleClose} centered enforceFocus={false}>
      <div className="ps-3 pe-3">
        {!showConfirm ? (
          <>
            <div className="text-center mb-4 mt-4">
              <h4 className="mt-3 mb-4">{title}</h4>
              <p className="grey w-75 m-auto">{description}</p>
            </div>
            <div className="d-flex gap-2 mb-3">
              <Button variant="outline-secondary" className="w-50" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" className="w-50" onClick={() => setShowConfirm(true)}>
                End Trial & Upgrade now
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center mb-4 mt-4">
              <h4 className="mt-3 mb-4">Confirm Upgrade</h4>
              <p className="grey w-75 m-auto">{confirmDescription(planName)}</p>
            </div>
            <div className="d-flex gap-2 mb-3">
              <Button
                variant="outline-secondary"
                className="w-50"
                onClick={() => setShowConfirm(false)}
                disabled={isUpgrading}
              >
                Cancel
              </Button>
              <Button variant="primary" className="w-50" onClick={handleUpgrade} disabled={isUpgrading}>
                {isUpgrading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </CustomModal>
  )
}

export default UpgradePromptModal
